import {ComponentInternalInstance, Directive, DirectiveBinding, VNode, watch} from 'vue'
import * as Yup from 'yup'
import {AnySchema} from 'yup' // Added ValidationError import
import {FormContextSymbol} from './index.ts'

// Create a symbol for the validation path context
export const ValidationPathSymbol = Symbol('ValidationPath')

export const vValidation: Directive = {
    created(el, binding, vnode) {
        if (binding.value) {
            const path = binding.value.path
            if (!path) throw new Error("When passing a value to v-validation it should be an object with a path property.")
            const parentPath = findParentValidationPath(el)
            el.dataset.validationPath = parentPath ? `${parentPath}.${path}` : path
            return
        }
    },
    mounted(el: HTMLElement, binding: DirectiveBinding) {
        setupValidation(el, binding)
    },

    updated(el: HTMLElement, binding: DirectiveBinding) {
        setupValidation(el, binding)
    },

    unmounted(el: HTMLElement) {
        // Clean up the watch when the directive is unmounted
        const elementWithData = el as HTMLElementWithVue & {
            _vValidationCleanup?: () => void
        }
        if (elementWithData._vValidationCleanup) {
            elementWithData._vValidationCleanup()
        }
    }
}

function setupValidation(el: HTMLElement, binding: DirectiveBinding) {
    const element = el as HTMLElementWithVue
    const parentComponent = element.__vueParentComponent?.parent
    if (!parentComponent) return

    const updateModelHandler = parentComponent.vnode.props?.['onUpdate:modelValue']
    let currentPath: string | undefined

    // Try to extract a path automatically if none is provided
    if (!binding.value || !binding.value.path) {
        if (updateModelHandler) {
            try {
                const handlerString = updateModelHandler.toString()
                const match = handlerString.match(/\.(.+?)(?:\s*=\s*|,|;)/)
                const fullPath = match ? match[1] : ''
                // Remove the first segment if needed (adjust based on your update handler format)
                currentPath = fullPath.split('.').slice(1).join('.')
            } catch (e) {
                console.warn("v-validation directive: Failed to extract path from updateModelHandler", e)
                currentPath = undefined
            }
        } else {
            console.warn("v-validation directive: No updateModelHandler found on parent component.")
            currentPath = undefined
        }
    } else {
        // Use the provided path
        currentPath = binding.value.path
    }

    if (!currentPath) {
        // If we couldn’t get a valid path, do nothing further.
        return
    }

    // Combine with parent's validation path if available
    const parentPath = findParentValidationPath(el)
    const path = parentPath ? `${parentPath}.${currentPath}` : currentPath
    el.dataset.validationPath = path

    // Access the provided form context (if available)
    const formContext = findProvider(parentComponent, FormContextSymbol)
    if (!formContext) {
        console.warn('Form context not found. Make sure the component is within a Form component.')
        return
    }

    const {validation, errors} = formContext
    let fieldSchema: AnySchema | null = null
    try {
        fieldSchema = Yup.reach(validation, path) as AnySchema
    } catch (err) {
        // If no schema is found, just continue silently.
        // Optionally, log for debugging:
        // console.info(`No schema found for path: ${path}`)
    }

    const isRequired = fieldSchema ? isSchemaRequired(fieldSchema) : false

    if (parentComponent.props) {
        // If the parent component accepts a "required" prop, set it accordingly.
        parentComponent.props.required = isRequired
    }

    // Watch for changes in the errors and update the parent's "error" prop accordingly.
    const stopWatch = watch(
        () => errors.value,
        () => {
            const pathError = errors.value[path]
            if (pathError && parentComponent?.props) {
                parentComponent.props.error = pathError
            } else if (parentComponent?.props) {
                parentComponent.props.error = null
            }
        },
        {deep: true, immediate: true}
    )

    // Store the stop function for cleanup
    const elementWithData = el as HTMLElementWithVue & {
        _vValidationCleanup?: () => void
        _vValidationPath?: string
    }
    if (elementWithData._vValidationCleanup) {
        elementWithData._vValidationCleanup()
    }
    elementWithData._vValidationCleanup = stopWatch
    elementWithData._vValidationPath = path
    return {path, stopWatch}
}

// Helper function to check if a schema has a required test
function isSchemaRequired(schema: AnySchema) {
    return schema.describe().tests.some(test => test.name === 'required')
}

function findProvider(instance: ComponentInternalInstance | null, key: symbol): any {
    if (!instance) return null

    // Check if this instance provides our key
    //@ts-ignore
    if (instance.provides && key in instance.provides) {
        //@ts-ignore
        return instance.provides[key]
    }

    // If not, check the parent
    return findProvider(instance.parent, key)
}

function findParentValidationPath(el: HTMLElement): string | null {
    let parent = el.parentElement
    while (parent) {
        // Stop if we hit the root validation element (form)
        if (parent.hasAttribute('data-validation-root')) {
            return null
        }
        if (parent.dataset.validationPath) {
            return parent.dataset.validationPath
        }
        parent = parent.parentElement
    }
    return null
}

interface EnhancedComponentInternalInstance extends ComponentInternalInstance {
    parent: ComponentInternalInstance & {
        props: Record<string, any>,
        vnode: VNode & {
            props?: {
                'onUpdate:modelValue'?: Function
            }
        },
        $: {
            [key: symbol]: any
        }
    }
}

interface HTMLElementWithVue extends HTMLElement {
    __vueParentComponent?: EnhancedComponentInternalInstance
}